import type { NavigateFunction } from 'react-router-dom';
import { store } from '../store';
import { setLocation } from '../store/slices/locationSlice';

type GateOptions = {
  /**
   * Open the address screen even when the location request yields nothing.
   * Use for explicit taps (the delivery row), where doing nothing would
   * look like a broken button. Leave off for implicit triggers like
   * add-to-cart, which should stay silent right after the user declines.
   */
  alwaysOpenMap?: boolean;
  /**
   * Where the address screen's back button should return to. Defaults to
   * the home screen.
   */
  returnTo?: string;
};

/**
 * Asks Telegram's LocationManager for the device geolocation (this is what
 * shows the native OS permission prompt) and routes to the address-picking
 * screen based on the outcome:
 * - granted -> open it centered on the real location
 * - access already requested before and denied (whether that decision was
 *   made just now or earlier, e.g. by another location prompt elsewhere in
 *   the app) -> open it centered on the capital fallback with the
 *   "enable geolocation in settings" alert, since the OS won't show its
 *   permission prompt again on its own once a decision has been made
 * - being asked for the very first time and the OS prompt is denied ->
 *   do nothing this attempt (the OS already showed its own dialog), unless
 *   `alwaysOpenMap` says the user explicitly asked for this screen
 */
export const requestCartLocationGate = (
  navigate: NavigateFunction,
  options?: GateOptions
): void => {
  // /adding-address, not the pickup map: it is the only screen with a
  // draggable pin, address search and save-by-picked-point. The pickup map
  // is branch selection only.
  const openAddressMap = (showPermissionAlert: boolean) => {
    navigate('/adding-address', {
      state: { showPermissionAlert, returnTo: options?.returnTo },
    });
  };

  const WebApp = window.Telegram?.WebApp;
  const LM = WebApp?.LocationManager;

  // Outside Telegram (or on a build without LocationManager) there's no
  // permission flow to run at all — an explicit tap should still land on
  // the screen, just without a pre-filled position.
  if (!WebApp || !LM) {
    if (options?.alwaysOpenMap) openAddressMap(false);
    return;
  }

  const runLogic = () => {
    if (LM.isAccessRequested && !LM.isAccessGranted) {
      openAddressMap(true);
      return;
    }

    LM.getLocation(
      (location: { latitude: number; longitude: number } | null) => {
        if (location) {
          store.dispatch(
            setLocation({
              latitude: location.latitude,
              longitude: location.longitude,
            })
          );
          openAddressMap(false);
          return;
        }

        if (options?.alwaysOpenMap) openAddressMap(true);
      }
    );
  };

  if (!LM.isInited) {
    LM.init(() => runLogic());
  } else {
    runLogic();
  }
};
